import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import FormInput from '../../components/FormInput/FormInput';
import Validator from '../../utils/validator';
import { login } from '../../services/auth';
import { login as loginAction } from './state/AuthSlice';
import './Login.scss';

import IconLogoHorizontal from '../../assets/icons/IconLogoHorizontal';
import IconUnvisible from '../../assets/icons/IconUnvisible';
import IconVisible from '../../assets/icons/IconVisible';
import LoginImage from '../../assets/login-illustrator.svg';

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [checkRemember, setcheckRemember] = useState(false);

  const schemaResolver = yupResolver(
    Validator.generateSchema({
      email: Validator.string()
        .trim()
        .required('Please enter Email')
        .email('Please enter valid Email'),
      password: Validator.string()
        .trim()
        .required('Please enter Password')
        .min(6, 'Minimum 6 symbols')
        .max(50, 'Maximum 50 symbols'),
    })
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({ resolver: schemaResolver });

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickRemember = () => setcheckRemember((show) => !show);

  const onSubmit = async (data) => {
    setIsLoading(true);
    const response = await login({ email: data?.email, password: data?.password });
    if (response.success) {
      dispatch(loginAction(response.data));
      navigate('/dashboard');
      setIsLoading(false);
    } else {
      console.error('Error::', response.message);
      setIsLoading(false);
    }
  };

  return (
    <Box className='login-form' display={'flex'} alignItems={'center'}>
      <Container
        // maxWidth="1280px"
        maxWidth='xl'
        fixed='false'
      >
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems={'center'} justifyContent={'center'}>
          <Grid
            size={{ xs: 12, md: 6, lg: 'auto', xl: 'auto' }}
            order={{ xs: 3, md: 1 }}
            display={{ xs: 'none', md: 'block' }}
          >
            <Box className='login-image'>
              <img src={LoginImage} alt={'SecAI'} loading='eager' />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 5, xl: 4 }} order={{ xs: 2, md: 2 }}>
            <Card>
              <CardContent>
                <Box
                  component={'form'}
                  padding={{ xs: 0, md: 1, lg: 1.5 }}
                  paddingBottom={{ xs: 0, md: 1, lg: 1.5 }}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Box mb={2}>
                    <IconLogoHorizontal width={'140px'} height={'42px'} />
                  </Box>

                  <Box mb={2}>
                    <Typography variant='h4' component={'h3'} color='text.primary' mb={0.25}>
                      Sign In
                    </Typography>
                    <Typography variant='body2' component={'p'} color='text.light'>
                      to your account
                    </Typography>
                  </Box>

                  <Box mb={2}>
                    <FormInput
                      label={'Email'}
                      fieldType={'text'}
                      type={'email'}
                      name={'email'}
                      placeholder={'Enter Email'}
                      required
                      control={control}
                      errors={errors}
                    />
                  </Box>

                  <Box mb={1.5}>
                    <FormInput
                      label={'Password'}
                      fieldType={'text'}
                      type={showPassword ? 'text' : 'password'}
                      name={'password'}
                      placeholder={'******'}
                      autoComplete='off'
                      endIcon={true}
                      required
                      endIconEle={
                        <InputAdornment position='end'>
                          <IconButton onClick={handleClickShowPassword} edge='end'>
                            {showPassword ? (
                              <IconVisible width={16} height={16} />
                            ) : (
                              <IconUnvisible width={16} height={16} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      control={control}
                      errors={errors}
                    />
                  </Box>

                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    gap={1}
                    mb={1}
                    pl={0.5}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          // defaultChecked
                          onClick={handleClickRemember}
                        />
                      }
                      label={
                        <Typography
                          variant='body2'
                          marginLeft={-0.5}
                          fontWeight={checkRemember ? 600 : 500}
                          color={checkRemember ? 'primary.main' : 'secondary.main'}
                        >
                          Remember Me
                        </Typography>
                      }
                    />

                    <Link
                      href='#'
                      variant='body2'
                      underline='hover'
                      color='secondary.main'
                      fontWeight='600'
                    >
                      Forgot Password?
                    </Link>
                  </Box>

                  <Box>
                    <Button
                      type='submit'
                      variant='contained'
                      size='large'
                      loading={isLoading}
                      loadingPosition='end'
                      fullWidth
                    >
                      Sign In
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }} order={{ xs: 1, md: 3 }}>
            <Typography variant='h1' component={'h1'} color='common.white' textAlign={'center'}>
              Fast, Efficient & Productive
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LoginPage;
